// Recent: answers sent from this phone and what happened after. Typed text shows for 24 hours (SPEC 5.5).
// Each entry says plainly what was sent and to what ("You denied: Write v309.txt"), with the effect's symbol and tint,
// the agent, the machine and the time. The newest is first and marked Latest (live test, 24 Oct 2026). An answer the
// PC hasn't confirmed says "Sending: …" or "Not sure it arrived: …" and wears that mark, never allow's or deny's.
import { useAppState } from "../app/AppContext";
import { useNow } from "../app/useNow";
import { recentHeadline, recentList, recentMark, visibleText } from "../lib/model";
import { ago, clock } from "../lib/time";
import { EffectMark } from "../components/EffectMark";
import { describeResult } from "../components/ResultView";
import { ConnectionChip, Page, TabBar, TopBar } from "../components/Shell";

export function Recent() {
  const s = useAppState();
  const now = useNow(5000);
  const list = recentList(s);
  return (
    <>
      <TopBar right={<ConnectionChip />} />
      <Page>
        <h2 className="label">Recent</h2>
        {list.length === 0 ? (
          <div className="empty">
            <h2>No answers yet.</h2>
            <p>Everything you send from this phone shows up here with what happened after it.</p>
          </div>
        ) : (
          <ul className="recent" aria-label="Answers from this phone, newest first">
            {list.map((rec, i) => {
              const d = describeResult(rec, now);
              const text = visibleText(rec, now);
              const mark = recentMark(rec);
              return (
                <li key={rec.commandId} className={`recent-item recent-${mark}${i === 0 ? " is-latest" : ""}`}>
                  <EffectMark effect={mark} />
                  <div className="body">
                    <b className="what">{recentHeadline(rec)}</b>
                    <span className="ctx">
                      {rec.agentName} · {rec.machineName} · {rec.envLabel}
                    </span>
                    <span className="sum">
                      {rec.summary} · <span className={`res tone-${d.tone}`}>{d.headline}</span>
                    </span>
                    {text ? <span className="txt">{text}</span> : rec.text ? <span className="ctx">Typed reply sent.</span> : null}
                  </div>
                  <div className="side">
                    {i === 0 && <span className="latest">Latest</span>}
                    <time dateTime={new Date(rec.startedAt).toISOString()}>
                      {when(rec.startedAt, now).map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                    </time>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Page>
      <TabBar />
    </>
  );
}

/** Two lines: how long ago, then the clock time. ["3 min ago", "14:32"], ["just now", "14:32"], ["yesterday 14:32"]. */
function when(at: number, now: number): string[] {
  const rel = ago(at, now);
  const first = /^\d/.test(rel) && now - at < 24 * 60 * 60_000 ? `${rel} ago` : rel;
  return rel.includes(":") ? [first] : [first, clock(at)];
}
